import { MoreHoriz } from '@mui/icons-material';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import React, { useState } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CalculsBox } from 'src/components/basic/CalculsBox';
import { CheckboxFormField } from 'src/components/forms/CheckboxFormField';
import { ComputedPropsForm } from 'src/components/forms/ComputedPropsForm';
import { ComputedReadFieldForm } from 'src/components/forms/ComputedReadFieldForm';
import { ComputedTextFieldForm } from 'src/components/forms/ComputedTextFieldForm';
import { ExpandableTextFieldForm } from 'src/components/forms/ExpandableTextFieldForm';
import { SelectFieldForm } from 'src/components/forms/SelectFieldForm';
import { TextFieldForm } from 'src/components/forms/TextFieldForm';
import { IMaskNumberProps, MaskNumber } from 'src/components/masks/MaskNumber';
import { LayoutTable, NormalTable, SpanNoWrap } from 'src/components/styled';
import { CalculsFormNewPerteGainsProfessionnelsActuels } from 'src/constants/calculs';
import { defaultPerteDeChanceDeGainProfessionnelRow } from 'src/constants/prejudice/defaultValues/FormPerteGainsProfessionnels.defaultValues';
import { fCurrency } from 'src/helpers/formatNumber';
import {
  getDisplayedQuotientRevalorisations,
  getMontantRevalorise,
  getQuotientRevalorisationTooltip,
} from 'src/helpers/prejudices/revalorisation';
import { useFormArrayUtils } from 'src/hooks/useFormArrayUtils';
import { MonetaryErosion } from 'src/types/monetaryErosion.type';
import {
  NewPrejudiceFormPerteGainProfessionnelsActuel,
  NumeroPieceValuesRow,
  unites,
} from 'src/types/prejudice.type';
import { TooltipTableHeader } from '../../TooltipTableHeader';
import { FormNumeroPieceDialog } from '../FormNumeroPieceDialog';

interface Props {
  control: Control<NewPrejudiceFormPerteGainProfessionnelsActuel>;
  allNumerosPieces: NumeroPieceValuesRow[];
  dateLiquidation: Date | undefined;
  monetaryErosions: MonetaryErosion[];
}

export const TotalPerteGainsProfessionnelsActuel: React.FC<Props> = ({
  control,
  allNumerosPieces,
  dateLiquidation,
  monetaryErosions,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);

  const { getValues, setValue } = useFormContext();

  const { duplicate, remove, fields, append } = useFormArrayUtils({
    control: control as Control<NewPrejudiceFormPerteGainProfessionnelsActuel>,
    name: 'perteDeChanceDeGainProfessionnel.rows',
    getValues,
    setValue,
  });

  const handleMenuOpen =
    (index: number) => (event: React.MouseEvent<HTMLElement>) => {
      setOpenMenuIndex(index);
      setAnchorEl(event.currentTarget);
    };

  const handleMenuClose = () => {
    setOpenMenuIndex(null);
    setAnchorEl(null);
  };

  const handleDelete = (index: number) => {
    remove(index);
    handleMenuClose();
  };

  const handleDuplicate = (index: number) => {
    duplicate(index);
    handleMenuClose();
  };

  const onAppendRow = () => {
    append(defaultPerteDeChanceDeGainProfessionnelRow);
  };

  return (
    <>
      <TableContainer>
        <NormalTable>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ width: '50px' }}>
                {t('numeroPiece.form.columnHeader')}
              </TableCell>
              <TableCell align="center" sx={{ width: '50px' }}>
                {t(
                  'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.perteDeChanceDeGainProfessionnel.rows.annee.columnHeader',
                )}
              </TableCell>
              <TableCell align="center">
                {t(
                  'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.perteDeChanceDeGainProfessionnel.rows.indemnite.columnHeader',
                )}
              </TableCell>
              <TableCell>
                {t(
                  'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.perteDeChanceDeGainProfessionnel.rows.commentaires.columnHeader',
                )}
              </TableCell>
              <TableCell align="center">
                {t(
                  'prejudice.revalorisation.columnHeaders.montantDejaRevalorise',
                )}
              </TableCell>
              <TableCell align="center">
                {t('prejudice.revalorisation.quotient.columnHeader')}
              </TableCell>
              <TableCell align="center" sx={{ minWidth: '150px' }}>
                {t(
                  'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.perteDeChanceDeGainProfessionnel.rows.indemnite.revalorise',
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((field, index) => (
              <TableRow key={field.id}>
                <TableCell align="center">
                  <FormNumeroPieceDialog
                    control={control}
                    name={`perteDeChanceDeGainProfessionnel.rows.${index}.numerosPieces`}
                    allOtherNumeroPieces={allNumerosPieces}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextFieldForm
                    control={control}
                    name={`perteDeChanceDeGainProfessionnel.rows.${index}.annee`}
                    InputProps={{
                      inputComponent: MaskNumber as any,
                      inputProps: {
                        numberMask: {
                          scale: 0,
                          min: 1900,
                          max: 2100,
                          thousandsSeparator: '',
                        },
                      } as IMaskNumberProps,
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell align="center" sx={{ width: '350px' }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                  >
                    <TextFieldForm
                      control={control}
                      name={`perteDeChanceDeGainProfessionnel.rows.${index}.indemnite`}
                      InputProps={{
                        inputComponent: MaskNumber as any,
                        inputProps: {
                          numberMask: { scale: 2 },
                          suffix: '€',
                        } as IMaskNumberProps,
                      }}
                    />
                    <SelectFieldForm
                      control={control}
                      name={`perteDeChanceDeGainProfessionnel.rows.${index}.unite`}
                      options={unites.map((unite) => ({
                        label: t(`prejudice.fields.unite.optionsPar.${unite}`),
                        value: unite,
                      }))}
                      sx={{ width: '130px' }}
                    />
                  </Stack>
                </TableCell>
                <TableCell>
                  <ExpandableTextFieldForm
                    control={control}
                    name={`perteDeChanceDeGainProfessionnel.rows.${index}.commentaires`}
                    dialogTitle={t('prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.perteDeChanceDeGainProfessionnel.rows.commentaires.columnHeader')}
                  />
                </TableCell>
                <ComputedReadFieldForm
                  control={control}
                  name={`perteDeChanceDeGainProfessionnel.rows.${index}.indemniteAnnuel`}
                  watchFields={[
                    `perteDeChanceDeGainProfessionnel.rows.${index}.indemnite`,
                    `perteDeChanceDeGainProfessionnel.rows.${index}.unite`,
                    `perteDeChanceDeGainProfessionnel.rows.${index}.annee`,
                    `perteDeChanceDeGainProfessionnel.rows.${index}.montantsDejaRevalorises`,
                  ]}
                  compute={([revenuNet, unite]) =>
                    CalculsFormNewPerteGainsProfessionnelsActuels.getRevenuActiviteAnnuelNetReference(
                      {
                        revenuNet,
                        unite,
                      },
                    )
                  }
                />
                <TableCell align="center">
                  <CheckboxFormField
                    control={control}
                    name={`perteDeChanceDeGainProfessionnel.rows.${index}.montantsDejaRevalorises`}
                  />
                </TableCell>
                <TableCell align="center">
                  <ComputedPropsForm
                    control={control}
                    watchFields={[
                      `perteDeChanceDeGainProfessionnel.rows.${index}.annee`,
                      `perteDeChanceDeGainProfessionnel.rows.${index}.montantsDejaRevalorises`,
                      'revalorisationCoefficientsType',
                    ]}
                    compute={([
                      annee,
                      montantsDejaRevalorises,
                      revalorisationCoefficientsType,
                    ]) => {
                      if (
                        montantsDejaRevalorises ||
                        !monetaryErosions ||
                        !dateLiquidation ||
                        !(annee || annee === 0)
                      ) {
                        return {
                          props: {
                            displayedQuotientRevalorisation: '',
                          },
                          hidden: true,
                        };
                      }
                      let displayedQuotientRevalorisation = '';

                      if (
                        revalorisationCoefficientsType === 'annuel' ||
                        revalorisationCoefficientsType === 'smic'
                      ) {
                        displayedQuotientRevalorisation =
                          getDisplayedQuotientRevalorisations({
                            anneeOrDateLiquidation: dateLiquidation,
                            anneeOrDateMontant: annee,
                            monetaryErosions,
                            coefficientsType: revalorisationCoefficientsType,
                          });
                      }
                      return {
                        props: {
                          displayedQuotientRevalorisation,
                        },
                      };
                    }}
                    render={(values) => {
                      const displayedQuotientRevalorisation =
                        values.displayedQuotientRevalorisation as string;
                      return (
                        <SpanNoWrap>
                          {displayedQuotientRevalorisation}
                        </SpanNoWrap>
                      );
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <ComputedPropsForm
                    control={control}
                    watchFields={[
                      `perteDeChanceDeGainProfessionnel.rows.${index}.indemniteAnnuel`,
                      `perteDeChanceDeGainProfessionnel.rows.${index}.montantsDejaRevalorises`,
                      `perteDeChanceDeGainProfessionnel.rows.${index}.annee`,
                      'revalorisationCoefficientsType',
                    ]}
                    compute={([
                      indemniteAnnuel,
                      montantsDejaRevalorises,
                      annee,
                      revalorisationCoefficientsType,
                    ]) => {
                      if (
                        montantsDejaRevalorises ||
                        !dateLiquidation ||
                        !(annee || annee === 0) ||
                        !indemniteAnnuel
                      ) {
                        return {
                          props: {
                            indemniteAnnuel,
                            montantRevalorise: null,
                            quotientRevalorisationTooltip: '',
                            displayMontantRevalorise: false,
                          },
                        };
                      }
                      return {
                        props: {
                          indemniteAnnuel,
                          montantRevalorise: getMontantRevalorise({
                            anneeOrDateLiquidation: dateLiquidation,
                            montant: indemniteAnnuel,
                            monetaryErosions: monetaryErosions,
                            anneeOrDateMontant: annee,
                            coefficientsType: revalorisationCoefficientsType,
                          }),
                          quotientRevalorisationTooltip:
                            getQuotientRevalorisationTooltip({
                              anneeOrDateLiquidation: dateLiquidation,
                              anneeOrDateMontant: annee,
                              monetaryErosions,
                              coefficientsType: revalorisationCoefficientsType,
                            }),
                          displayMontantRevalorise: true,
                        },
                      };
                    }}
                    render={({
                      indemniteAnnuel,
                      montantRevalorise,
                      quotientRevalorisationTooltip,
                      displayMontantRevalorise,
                    }) =>
                      !displayMontantRevalorise ? (
                        <SpanNoWrap>{fCurrency(indemniteAnnuel)}</SpanNoWrap>
                      ) : (
                        <Tooltip
                          title={quotientRevalorisationTooltip || ''}
                          placement="top"
                        >
                          <SpanNoWrap>
                            {montantRevalorise !== null
                              ? fCurrency(montantRevalorise)
                              : null}
                          </SpanNoWrap>
                        </Tooltip>
                      )
                    }
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={handleMenuOpen(index)}>
                    <MoreHoriz />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={openMenuIndex === index}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={() => handleDelete(index)}>
                      {t('forms.table.deleteRow', { index: index + 1 })}
                    </MenuItem>
                    <MenuItem onClick={() => handleDuplicate(index)}>
                      {t('forms.table.duplicateRow', { index: index + 1 })}
                    </MenuItem>
                  </Menu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </NormalTable>
      </TableContainer>
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        paddingTop={2}
      >
        <Button variant="contained" onClick={onAppendRow}>
          {t('forms.table.addRow')}
        </Button>
      </Stack>
      <Stack alignItems="end" padding={2}>
        <CalculsBox>
          <LayoutTable>
            <TableBody>
              <TableRow>
                <TableCell align="left">
                  {t(
                    'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.perteDeChanceDeGainProfessionnel.totalIndemnite.columnHeader',
                  )}
                </TableCell>
                <TableCell align="right">
                  <ComputedTextFieldForm
                    control={control}
                    name="perteDeChanceDeGainProfessionnel.totalIndemnite"
                    watchFields={[
                      'perteDeChanceDeGainProfessionnel.rows',
                      'revalorisationCoefficientsType',
                    ]}
                    compute={([rows, revalorisationCoefficientsType]) => {
                      const mappedRows = (rows || []).map((row: any) => ({
                        numerosPieces: row.numerosPieces,
                        revenuActiviteAnnuelNet: row.indemniteAnnuel ?? 0,
                        annee: row.annee,
                        revenuNet: row.indemnite ?? 0,
                        unite: row.unite ?? '',
                        montantsDejaRevalorises:
                          row.montantsDejaRevalorises ?? false,
                      }));
                      const total =
                        CalculsFormNewPerteGainsProfessionnelsActuels.getTotalRevenuDeReferenceAnnuel(
                          {
                            rows: mappedRows,
                            revalorisationCoefficientsType,
                            dateLiquidation,
                            monetaryErosions,
                          },
                        );
                      return total;
                    }}
                    InputProps={{
                      inputComponent: MaskNumber as any,
                      inputProps: {
                        numberMask: { scale: 2 },
                        suffix: '€',
                      } as IMaskNumberProps,
                    }}
                    editedFieldsName="editedFields"
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left">
                  <TooltipTableHeader
                    tooltipTitle={t(
                      'prejudice.perteDeChance.fields.coefficientPerteDeChance.headerTooltip',
                    )}
                    headerLabel={t(
                      'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.perteDeChanceDeGainProfessionnel.coefficientPerteDeChance.columnHeader',
                    )}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextFieldForm
                    control={control}
                    name="perteDeChanceDeGainProfessionnel.coefficientPerteDeChance"
                    InputProps={{
                      inputComponent: MaskNumber as any,
                      inputProps: {
                        numberMask: { scale: 2, min: 0, max: 100 },
                        suffix: '%',
                      } as IMaskNumberProps,
                    }}
                    fullWidth
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="left">
                  {t(
                    'prejudice.prejudicesFormTypes.NEW_PERTE_GAINS_PROFESSIONELS.fields.perteDeChanceDeGainProfessionnel.perteDeChance.columnHeader',
                  )}
                </TableCell>
                <TableCell align="right">
                  <ComputedTextFieldForm
                    control={control}
                    name="perteDeChanceDeGainProfessionnel.perteDeChance"
                    watchFields={[
                      'perteDeChanceDeGainProfessionnel.totalIndemnite',
                      'perteDeChanceDeGainProfessionnel.coefficientPerteDeChance',
                    ]}
                    compute={([totalIndemnite, coefficientPerteDeChance]) =>
                      CalculsFormNewPerteGainsProfessionnelsActuels.getPerteDeChanceDeGainProfessionnelsPerteDeChance(
                        {
                          totalIndemnite,
                          coefficientPerteDeChance,
                        },
                      )
                    }
                    InputProps={{
                      inputComponent: MaskNumber as any,
                      inputProps: {
                        numberMask: { scale: 2 },
                        suffix: '€',
                      } as IMaskNumberProps,
                    }}
                    editedFieldsName="editedFields"
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </LayoutTable>
        </CalculsBox>
      </Stack>
    </>
  );
};
